import React, { useState, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';

import { HOTEL_STATUSES } from 'constants/booking';
import { calculateMilesUsed, getHotelsStatuses } from 'utils/booking';
import { GET_HOTEL_BOOKINGS_INFO, HOTEL_BOOKING } from 'store/queries';
import { CANCEL_CONFIRMED_BOOKING, CANCEL_BOOKING, UPDATE_SALESFORCE_BOOKING_STATUS } from 'store/mutations';
import { historyShape, bookingShape, userShape } from 'constants/propTypes';
import { ACTION_CODES } from 'constants/errors';

import ForcedCancellationModal from 'components/shared/ForcedCancellationModal';
import OnHoldCancellationModal from 'components/shared/OnHoldCancellationModal';
import ThirdPartyBookingCancellationModal from 'components/shared/ThirdPartyBookingCancellationModal';
import { ToastContext } from 'components/ui/Toast';
import { ActionButton } from 'components/ui/Button';

import AgentPortalError from 'errors/AgentPortalError';
import { bookingStatus } from 'types/booking';
import { useManagedMutation } from 'utils/managedHooks';
import { getRefundsURL, getBookingJourneysURL, getBookingSearchURL } from 'utils/urls';

import { showCancelThirdPartyBookingWarning } from 'utils/booking';
import { bookingContainsSuspendedPassengers } from 'utils/bookingContainsSuspendedPassengers';
import { bookingHasPlaceholderTickets } from 'utils/bookingHasPlaceholderTickets';

const { PROVISIONAL, ON_HOLD, TEMPORARILY_ON_HOLD, EXPIRED_ON_HOLD } = bookingStatus;

const CancelBookingCta = ({
  user,
  overrideAftersalesRules,
  bookingReference,
  intl: { formatMessage },
  disabled,
  history,
  status,
  booking,
  selectedItemRefs
}) => {
  const isDotwBooking = booking?.hotels?.[0]?.isDotwBooking;
  const { createToast } = useContext(ToastContext) || {};
  const [isOpen, setOpen] = useState(false);
  const [notFoundHotelError, setNotFoundHotelError] = useState(false);

  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const isOnHold = [ON_HOLD, TEMPORARILY_ON_HOLD].includes(status);
  const isExpiredOnHold = [EXPIRED_ON_HOLD].includes(status);
  const isRefunds = history.location.pathname === getRefundsURL(bookingReference);
  const isDisabled =
    isRefunds ||
    disabled ||
    bookingContainsSuspendedPassengers({ booking }) ||
    isExpiredOnHold ||
    bookingHasPlaceholderTickets({ booking });
  const isProvisional = status === PROVISIONAL;
  const cancelThirdPartyBooking = showCancelThirdPartyBookingWarning({ booking, user, selectedItemRefs });
  const requiresWarning = overrideAftersalesRules || isOnHold || (cancelThirdPartyBooking && !overrideAftersalesRules && !isOnHold);
  const milesUsed = calculateMilesUsed(booking);

  const { data: hotelsData, ...getHotelsState } = useQuery(isDotwBooking ? HOTEL_BOOKING : GET_HOTEL_BOOKINGS_INFO, {
    variables: isDotwBooking ? { reference: bookingReference } : { bookingReference }
  });

  const [cancelConfirmedBooking, cancelConfirmedBookingState] = useManagedMutation(CANCEL_CONFIRMED_BOOKING, {
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });

  const [cancelProvisionalBooking, cancelProvisionalBookingState] = useManagedMutation(CANCEL_BOOKING, {
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });

  const [updateSalesforceBookingStatus, updateSalesforceBookingStatusState] = useManagedMutation(UPDATE_SALESFORCE_BOOKING_STATUS, {
    variables: {
      bookingReference,
      status: 'CANCELLED'
    }
  });

  const states = [cancelConfirmedBookingState, cancelProvisionalBookingState, getHotelsState, updateSalesforceBookingStatusState];
  const loading = states.some((item) => item.loading);

  const hotelStatuses =
    (hotelsData?.getHotelBookings || hotelsData?.hotelBooking) &&
    getHotelsStatuses(booking?.hotels, isDotwBooking ? hotelsData.hotelBooking : hotelsData.getHotelBookings, isDotwBooking);
  const isHotelNotFoundInJacTravel = hotelStatuses?.find((h) => h.jacTravelStatus === HOTEL_STATUSES.NOT_FOUND);

  if (notFoundHotelError) {
    throw new Error(`Hotel with JacTravel reference: ${booking?.hotels[0].jacTravelRef} is not found in JacTravel`);
  }

  const handleResponse = async ({ cancelBookingItems = {}, cancelBooking = false }) => {
    if (cancelBookingItems.cancellable || cancelBooking) {
      if (milesUsed) {
        await updateSalesforceBookingStatus();
      }

      return history.push(isProvisional ? getBookingSearchURL(membershipNumber) : getRefundsURL(bookingReference, membershipNumber));
    }

    const error = new Error(
      formatMessage({
        id: 'manageBooking.cannotCancelAllTickets'
      })
    );

    if (createToast) {
      return createToast(error.message);
    }

    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: getBookingJourneysURL(bookingReference, membershipNumber)
    });
  };

  const handleForcedSubmit = async ({ overrideReason }) => {
    const { data } = await cancelConfirmedBooking({
      variables: {
        bookingReference,
        overrideReason,
        overrideAftersalesRules: true
      }
    });

    setOpen(false);
    await handleResponse(data);
  };

  const handleOnHoldSubmit = async () => {
    const { data } = await cancelConfirmedBooking({
      variables: {
        bookingReference
      }
    });

    setOpen(false);
    await handleResponse(data);
  };

  const handleClick = async () => {
    if (isHotelNotFoundInJacTravel) {
      setNotFoundHotelError(true);

      return;
    }

    if (requiresWarning) {
      setOpen(true);

      return;
    }

    if (isProvisional) {
      const { data } = await cancelProvisionalBooking({
        variables: {
          bookingReference
        }
      });

      await handleResponse(data);
    } else {
      const { data } = await cancelConfirmedBooking({
        variables: {
          bookingReference
        }
      });

      await handleResponse(data);
    }
  };

  const handleToggle = () => {
    setOpen(!isOpen);
  };

  return (
    <>
      <ActionButton disabled={isDisabled || loading} loading={loading} data-cy="cancel-refund" onClick={handleClick}>
        <FormattedMessage id="manageBooking.cancelRefund" />
      </ActionButton>
      {cancelThirdPartyBooking && !overrideAftersalesRules && !isOnHold && (
        <ThirdPartyBookingCancellationModal isOpen={isOpen} isLoading={loading} onToggle={handleToggle} onSubmit={handleOnHoldSubmit} />
      )}

      {overrideAftersalesRules && (
        <ForcedCancellationModal
          isOpen={isOpen}
          isLoading={loading}
          onToggle={handleToggle}
          onSubmit={handleForcedSubmit}
          booking={booking}
          selectedItemRefs={selectedItemRefs}
          mergeThirdPartyModal={cancelThirdPartyBooking}
        />
      )}

      {!overrideAftersalesRules && isOnHold && (
        <OnHoldCancellationModal
          isOpen={isOpen}
          isLoading={loading}
          onToggle={handleToggle}
          onSubmit={handleOnHoldSubmit}
          mergeThirdPartyModal={cancelThirdPartyBooking}
          status={status}
        />
      )}
    </>
  );
};

CancelBookingCta.propTypes = {
  overrideAftersalesRules: PropTypes.bool,
  bookingReference: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  history: historyShape.isRequired,
  status: PropTypes.string,
  intl: PropTypes.shape({ formatMessage: PropTypes.func }).isRequired,
  booking: bookingShape,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  user: userShape
};

export default CancelBookingCta;
